import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendUp, TrendDown, CircleNotch } from "@phosphor-icons/react";
import CandleChart from "./CandleChart";
import { BACKEND_BASE_URL } from "../lib/api";

const publicOhlc = axios.create({ baseURL: `${BACKEND_BASE_URL}/api` });

const PERIODS = [
  { label: "1M", value: "1mo" },
  { label: "3M", value: "3mo" },
  { label: "6M", value: "6mo" },
  { label: "1Y", value: "1y" },
  { label: "3Y", value: "3y" },
];

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : "—";

export default function IndexDetailModal({ index, onClose }) {
  const [period, setPeriod] = useState("6mo");
  const [ohlc, setOhlc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!index?.symbol) return;
    setLoading(true);
    setOhlc(null);
    // Use publicApi since this modal can be opened from landing page (pre-auth)
    publicOhlc
      .get(`/ohlc/${encodeURIComponent(index.symbol)}?period=${period}`)
      .then(({ data }) => setOhlc(data))
      .catch(() => setOhlc(null))
      .finally(() => setLoading(false));
  }, [index?.symbol, period]);

  if (!index) return null;
  const pos = index.change_pct >= 0;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="index-detail-modal"
      >
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 16, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="relative bg-surface border border-subtle rounded-sm w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            data-testid="index-modal-close"
            className="absolute top-4 right-4 p-2 text-muted-fg hover:text-primary-fg hover:bg-surface-hover rounded-sm"
          >
            <X size={16} />
          </button>

          {/* Header */}
          <div className="flex items-start justify-between pr-10 mb-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                /// Live index · {index.symbol}
              </div>
              <h3 className="font-mono text-3xl font-semibold tracking-wide text-primary-fg">
                {index.name}
              </h3>
            </div>
            <div className="text-right">
              <div className="font-mono text-3xl tabular-nums text-primary-fg">{fmt(index.price)}</div>
              <div
                className={
                  "text-sm font-mono tabular-nums flex items-center justify-end gap-1 " +
                  (pos ? "text-pos" : "text-neg")
                }
              >
                {pos ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                {index.change >= 0 ? "+" : ""}
                {fmt(index.change)} ({pos ? "+" : ""}
                {index.change_pct.toFixed(2)}%)
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mt-1">
                prev close ₹{fmt(index.prev_close)}
              </div>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-2 mb-4">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.value}`}
                className={
                  "px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                  (period === p.value
                    ? "bg-pos-soft border-pos-soft text-pos"
                    : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg")
                }
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Chart */}
          {loading && !ohlc ? (
            <div className="h-[440px] flex items-center justify-center border border-subtle rounded-sm bg-app">
              <CircleNotch className="animate-spin text-mod" size={16} weight="bold" />
              <span className="ml-2 text-sm text-secondary-fg">Loading candles…</span>
            </div>
          ) : ohlc?.bars?.length ? (
            <CandleChart bars={ohlc.bars} ema10={ohlc.ema10} height={440} />
          ) : (
            <div className="h-[300px] flex items-center justify-center border border-subtle rounded-sm bg-app text-sm text-muted-fg">
              Chart data unavailable.
            </div>
          )}

          {/* Footer */}
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={`https://in.tradingview.com/chart/?symbol=${
                index.code === "SENSEX" ? "BSE%3ASENSEX" : `NSE%3A${index.code}`
              }`}
              target="_blank"
              rel="noreferrer"
              data-testid="index-tradingview"
              className="px-4 py-2 rounded-sm text-sm font-medium transition"
              style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
            >
              Open in TradingView ↗
            </a>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-subtle rounded-sm text-primary-fg hover:bg-surface-hover hover:border-focus transition text-sm"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
